import React, { FC, useCallback, useRef } from 'react'
import { useReactiveVar } from '@apollo/client'

import { ArchivedAlert } from 'components'

import { ArtistInfo, ProjectEditButton, ProjectNavTabs } from 'modules/Projects/containers'
import { GlobalizeButton } from 'modules/Globalize/containers'
import { useLocalProjectFlags } from 'modulesCommon/serviceHooks'
import { useScroll } from 'shared/hooks'
import { activeProjectVar } from 'modulesCommon/store'
import { SBX_MAX_HEADER_HEIGHT, SBX_MIN_HEADER_HEIGHT } from 'shared/utils/header'
import { ENABLED_FEATURES } from 'shared/utils/featureFlags'
import './ProjectDetailsHeader.scss'
import { Can } from 'shared/abilities'
import { subject } from '@casl/ability'

const BREAK_POINT_1 = SBX_MAX_HEADER_HEIGHT - SBX_MIN_HEADER_HEIGHT

const BREAK_POINT_FINAL = 233

const ProjectDetailsHeader: FC = () => {
  const container = useRef<HTMLDivElement>()

  const p = useReactiveVar(activeProjectVar)
  const activeProject = { ...p }
  const { isLocalized } = useLocalProjectFlags(activeProject)

  const [refProxy] = useScroll((ref, offset, lastCall) => {
    if (offset <= BREAK_POINT_1) {
      ref.style.setProperty('--sbx-scroll-top', offset + 'px')

      // Complete prev animation
      ref.style.setProperty('--sbx-scroll-height', '0px')
      ref.style.setProperty('--sbx-scroll-opacity', '1')
    }
    if (offset > BREAK_POINT_1) {
      ref.style.setProperty('--sbx-scroll-height', offset - BREAK_POINT_1 + 'px')
      ref.style.setProperty('--sbx-scroll-opacity', (1 - (offset - BREAK_POINT_1) / 110).toString())

      // Complete prev animation
      ref.style.setProperty('--sbx-scroll-top', BREAK_POINT_1 + 'px')
    }
    if (lastCall) {
      ref.classList.add('project-detail-header_complete')
    } else if (ref.classList.contains('project-detail-header_complete')) {
      ref.classList.remove('project-detail-header_complete')
    }
  }, BREAK_POINT_FINAL)

  const proxyContainer = useCallback(
    (el: HTMLHeadingElement) => {
      container.current = el
      refProxy(el)
    },
    [refProxy]
  )

  return (
    <div ref={proxyContainer} className="project-detail-header">
      <div className="project-detail-header__container" data-edit-mode-header>
        <div className="project-detail-header__wrapper">
          <ArtistInfo />
          <div className="project-detail-header__alerts">
            {activeProject.active === false ? <ArchivedAlert /> : null}
          </div>
          <div data-edit-mode className="project-detail-header__actions">
            <Can I={'edit'} this={subject('Project', activeProject)}>
              <ProjectEditButton />
            </Can>
            {ENABLED_FEATURES.GLOBALHUB && activeProject.active ? <GlobalizeButton /> : null}
          </div>
          <ProjectNavTabs isLocalized={isLocalized} />
        </div>
      </div>
    </div>
  )
}

export default ProjectDetailsHeader
